import PropTypes from "prop-types";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  getConnectionActionLabel,
  isConnectionActionPrimary,
  shouldShowConnectedChannelAction,
} from "@/lib/connectionStatus";
import {
  PAIRING_CHANNEL_KINDS,
  TELEGRAM_CONFIGURE_KINDS,
  LINKEDIN_CONFIGURE_KINDS,
  QUORA_CONFIGURE_KINDS,
  isSettingsConfigurableKind,
} from "@/lib/channelRegistry";
import { ConnectionChannelDetail } from "@/pages/settings/ConnectionChannelDetail";

export function ConnectionChannelCard({
  c,
  row,
  status,
  googleOauthStatus,
  canEdit,
  disconnectBusy,
  pairingBusy,
  telegramSyncBusy,
  onDisconnect,
  onAction,
  onReconfigure,
  actionTitle,
  StatusBadge,
}) {
  const I = c.Icon;
  const showDisconnect = status === "connected" && isSettingsConfigurableKind(c.kind);
  // Connected cards: Disconnect only — hide Manage / Reconnect / Reconfigure.
  const showPrimaryAction = status !== "connected" || shouldShowConnectedChannelAction(c.kind);
  const showTelegramReconfigure = (
    TELEGRAM_CONFIGURE_KINDS.has(c.kind)
    && status === "sync_failed"
    && typeof onReconfigure === "function"
  );

  return (
    <div
      className="rounded-xl border border-slate-200 bg-white p-4"
      data-testid={`connection-card-${c.kind}`}
    >
      <div className="flex items-start gap-3">
        <div className={cn("h-9 w-9 rounded-lg flex items-center justify-center shrink-0", c.iconBg)}>
          <I size={17} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[13.5px] font-semibold text-slate-900">{c.label}</div>
            <StatusBadge status={status} />
          </div>
          {row?.id && (
            <div className="mt-1.5 text-[10.5px] font-mono text-slate-400 truncate" title={row.id}>
              id: {row.id.slice(0, 8)}…
            </div>
          )}
          <div className="mt-2 text-[11.5px] text-slate-500 leading-snug">
            <ConnectionChannelDetail
              c={c}
              row={row}
              status={status}
              googleOauthStatus={googleOauthStatus}
            />
          </div>
        </div>
      </div>
      {(showDisconnect || showPrimaryAction || showTelegramReconfigure) && (
        <div className="mt-3 flex flex-col-reverse sm:flex-row items-stretch sm:items-center justify-end gap-2">
          {showDisconnect && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-9 sm:h-7 text-[11px] w-full sm:w-auto text-slate-600 hover:text-rose-700 hover:border-rose-300 hover:bg-rose-50"
              onClick={() => onDisconnect(c, row)}
              disabled={!canEdit || disconnectBusy === c.kind}
              data-testid={`connection-disconnect-${c.kind}`}
            >
              {disconnectBusy === c.kind ? "Disconnecting…" : "Disconnect"}
            </Button>
          )}
          {showTelegramReconfigure && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              className="h-9 sm:h-7 text-[11px] w-full sm:w-auto"
              disabled={
                !canEdit
                || !isSettingsConfigurableKind(c.kind)
                || !row?.id
              }
              title={canEdit
                ? "Discard incomplete setup and configure a new bot token"
                : "Admins only"}
              onClick={() => onReconfigure(c, row)}
              data-testid={`connection-reconfigure-${c.kind}`}
            >
              Re-Configure
            </Button>
          )}
          {showPrimaryAction && (
            <Button
              size="sm"
              variant={isConnectionActionPrimary(status) ? "default" : "outline"}
              className="h-9 sm:h-7 text-[11px] w-full sm:w-auto"
              disabled={
                status === "coming_soon" ||
                !canEdit ||
                !isSettingsConfigurableKind(c.kind) ||
                (PAIRING_CHANNEL_KINDS.has(c.kind) && (!row?.id || pairingBusy)) ||
                (TELEGRAM_CONFIGURE_KINDS.has(c.kind) && telegramSyncBusy) ||
                ((TELEGRAM_CONFIGURE_KINDS.has(c.kind) || LINKEDIN_CONFIGURE_KINDS.has(c.kind) || QUORA_CONFIGURE_KINDS.has(c.kind)) && !row?.id)
              }
              title={status === "coming_soon" ? "Coming soon" : actionTitle(c, status)}
              onClick={() => onAction(c, row)}
              data-testid={`connection-action-${c.kind}`}
            >
              {getConnectionActionLabel(status, c.kind)}
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

ConnectionChannelCard.propTypes = {
  c: PropTypes.shape({
    kind: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    iconBg: PropTypes.string,
    Icon: PropTypes.elementType,
  }).isRequired,
  row: PropTypes.object,
  status: PropTypes.string.isRequired,
  googleOauthStatus: PropTypes.object,
  canEdit: PropTypes.bool.isRequired,
  disconnectBusy: PropTypes.string,
  pairingBusy: PropTypes.bool,
  telegramSyncBusy: PropTypes.bool,
  onDisconnect: PropTypes.func.isRequired,
  onAction: PropTypes.func.isRequired,
  onReconfigure: PropTypes.func,
  actionTitle: PropTypes.func.isRequired,
  StatusBadge: PropTypes.elementType.isRequired,
};
